import {Box, DropdownItem} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {VerifyUserAndCompanyPermission} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Stepper from 'src/components/stepper'
import {StepsTitles} from 'src/components/stepper/StepperProps'
import useObjectsTypes from 'src/hooks/api/objects/useObjectTypes'
import {StepperModalContainer} from 'src/pages/camera-config/components/object-detection/components/styles'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'
import {CreateTriggerDTO} from 'src/services/api/endpoints/BrainBoxEndpoint'
import {BarrierType, CameraHasObjects, SpeedDetectionCoordinateDTO} from 'src/services/api/endpoints/ObjectEndpoint'
import {CreateObjectDTO, CreateSpeedDetectionObject, EditObjectDTO, EditSpeedDetectionObject} from '../../../../../../common/models/camera/CreateObjectDTO'
import api, {CreateBarrier, Server} from '../../../../../../services/api'
import {handleFacePermission} from '../../../../../../utilities/enums/listTypeDetectionColorEnum'
import handleErrorWithSnackbar from '../../../../../../utilities/handleErrorWithSnackbar'
import {MappedActions} from '../../ObjectDetection'
import {
  detectionDisableLineCrossing,
  detectionsToDrawArea,
  DispatchAllAndBlockUnknownIds,
  EnumLprIntegrations,
  skipFourthStep,
  skipThirdStep,
} from './components/Constants'
import WarningModal from './components/WarningModal'
import ChooseBarrierStep from './components/choose-barrier-step/ChooseBarrierStep'
import ConfigStepObjectPersonalized from './components/config-step-personalized/ConfigStepPersonalized'
import ConfigStep from './components/config-step/ConfigStep'
import CreateMultipleActions from './components/create-multiple-actions/CreateMultipleActions'
import {MultipleActionsType} from './components/create-multiple-actions/types/CreateMultipleActionsTypes'
import DetectionTypeStep from './components/detection-type-step/DetectionTypeStep'
import DrawAreaStep from './components/draw-barrier-step/DrawBarrierStep'

type StepperModalProps = {
  closeModal: (isOpen: boolean) => void
  registeredObjects: CameraHasObjects[] | undefined
  isEdit: boolean
  editingObject?: CameraHasObjects
  mappedActions: MappedActions[]
  servers: Server[]
  updateTable: () => void
}

type ChildrenComponentEnumType = {
  [key: number]: JSX.Element
}

export type ChosenObject = {
  detectionTypeChosen?: string
  confidence: number
  interestAreaId?: number
  isActive: boolean
  objectTypeId: number
  trigger?: CreateTriggerDTO[]
  threshold: number
  isObjectCounting?: boolean
  distance?: number
  cameraId?: number
  coordinates?: SpeedDetectionCoordinateDTO[]
  speedLimit?: number
  speedObjectId?: number
  id?: number
  type: BarrierType
  maximumTime?: number
  isObjectPermanency?: boolean
  detectionsOptionsSelected?: Array<string>
}

const DetectionsTypesWithLPRIntegrations = ['Placa', 'Leitura de Placas']

const StepperModal = (props: StepperModalProps) => {
  const {closeModal, registeredObjects, isEdit, editingObject, mappedActions, servers, updateTable} = props
  const {camera, refetchCamera} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()

  const [objectChosenToCreate, setObjectChosenToCreate] = useState<ChosenObject | null>(null)

  const [outputServersItems, setOutputServersItems] = useState<DropdownItem[]>([])
  const [actions, setActions] = useState<MultipleActionsType[]>([])
  const [warningModalOpen, setWarningModalOpen] = useState(false)

  const hasReadLineCrossingPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'line-crossing:read')
  const companyHasTelegramPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'telegram:create')
  const companyHasWhatsAppPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'whatsapp:create')
  const companyHasLPRIntegrationPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'lpr-integration:create')

  const setDiffOnChosenObject = useCallback((newChosen: Partial<ChosenObject>) => {
    setObjectChosenToCreate((chosen) => {
      if (chosen !== null) return {...chosen, ...newChosen}
      return null
    })
  }, [])

  const nextFunction = (index: number, resume: string, advanceTo?: number) => {
    const newSteps = [...steps]
    newSteps[index - 1].resume = resume
    newSteps[index - 1].stepperStatus = 'concluded'
    if (advanceTo) newSteps[advanceTo].stepperStatus = 'active'
    else newSteps[index].stepperStatus = 'active'
    setSteps(newSteps)
  }

  const returnFunction = (index: number, goBackTo?: number) => {
    const newSteps = [...steps]
    delete newSteps[index + 1].resume
    newSteps[index + 1].stepperStatus = 'pending'
    if (goBackTo || goBackTo === 0) newSteps[goBackTo].stepperStatus = 'active'
    else newSteps[index].stepperStatus = 'active'
    setSteps(newSteps)
  }

  const finishFunction = async (skipUpdate?: boolean) => {
    try {
      if (!objectChosenToCreate) return
      if (isEdit) return finishFunctionWhenEditing()

      if (objectChosenToCreate?.detectionTypeChosen === 'Velocidade') {
        const interestArea = new CreateBarrier(objectChosenToCreate.cameraId!, objectChosenToCreate.coordinates!, 'Velocidade', 'speed')
        const areaCreated = await api.barriers.createBarrier(interestArea)
        const speedObject = new CreateSpeedDetectionObject(objectChosenToCreate, areaCreated.data.data.id)

        const objectResponse = await api.object.createAllTypesObjects(speedObject)
        enqueueSnackbar('Objeto salvo com sucesso', {
          variant: 'success',
        })
        closeModal(false)
        return objectResponse.data.data
      }

      const newDetectionToCreate = new CreateObjectDTO(objectChosenToCreate)
      const objectResponse = await api.object.createAllTypesObjects(newDetectionToCreate)
      enqueueSnackbar('Objeto salvo com sucesso', {
        variant: 'success',
      })
      closeModal(false)
      return objectResponse.data.data
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao criar o objeto')
    } finally {
      if (!skipUpdate) {
        updateTable()
        refetchCamera()
      }
    }
  }

  const finishFunctionWhenEditing = async () => {
    if (!objectChosenToCreate) return
    const detection = objectChosenToCreate?.detectionTypeChosen
    const editedObject = detection === 'Velocidade' ? new EditSpeedDetectionObject(objectChosenToCreate) : new EditObjectDTO(objectChosenToCreate)

    const objectResponse = await api.object.updateAllTypesObjects(editedObject)
    enqueueSnackbar('Objeto salvo com sucesso', {
      variant: 'success',
    })
    closeModal(false)
    return objectResponse.data.data
  }

  const {data: getAll} = useObjectsTypes()

  const findObjectType = async () => {
    let detection = objectChosenToCreate?.detectionTypeChosen
    if (detection === 'Reconhecimento Facial') detection = 'Facial'
    if (detection === 'Leitura de Placas') detection = 'Placa'
    if (detection === 'Tempo de Permanência') detection = 'Pessoa'

    const objectsTypes = getAll?.find((it) => it.label.toLowerCase() === detection?.toLowerCase())
    if (objectsTypes) setDiffOnChosenObject({objectTypeId: objectsTypes.id})
  }

  const populateOutput = useCallback(
    (detection: string) => {
      const items: DropdownItem[] = servers.map((it) => {
        return {
          id: String(it.id),
          label: it.serverType?.description ?? it.name,
        }
      })

      items.unshift({id: '-5', label: 'E-mail'})
      if (companyHasTelegramPermission) items.unshift({id: '-2', label: 'Telegram'})
      if (companyHasWhatsAppPermission) items.unshift({id: '-6', label: 'WhatsApp'})

      if (DetectionsTypesWithLPRIntegrations.includes(detection) && companyHasLPRIntegrationPermission) items.push(...EnumLprIntegrations)

      setOutputServersItems(items.filter((value, index, array) => array.indexOf(value) === index))
    },
    [servers],
  )

  function populateActions(filteredActions: MappedActions, servers: Server[], objectChosenToCreate: ChosenObject) {
    const actions: MultipleActionsType[] = []
    filteredActions.objectAction.map((action) => {
      const colorOnList = action.actionColor

      const actionCard: MultipleActionsType = {
        idToRemove: action.idToDelete,
        fetchInfoFrom: action.fetchVehicleInfoFromFipe ? 'Fipe Brasil' : action.fetchVehicleInfoFromSinesp ? 'Sinesp' : '',
        listId: action.listId ? action.listId : action.dispatchAllPlates ? DispatchAllAndBlockUnknownIds.TODAS : undefined,
        listType: handleFacePermission(colorOnList),
      }

      let integrationLpr
      if (action.dispatchPlateToHarpia) integrationLpr = EnumLprIntegrations.find((it) => it.dispatchToHarpia === true)
      if (action.dispatchPlateToSpia) integrationLpr = EnumLprIntegrations.find((it) => it.dispatchToSpia === true)
      if (action.dispatchPlateToSivana) integrationLpr = EnumLprIntegrations.find((it) => it.dispatchToSivana === true)
      if (action.dispatchPlateToAspide) integrationLpr = EnumLprIntegrations.find((it) => it.dispatchToAspide === true)
      if (action.dispatchPlateToMuralhaPaulista) integrationLpr = EnumLprIntegrations.find((it) => it.dispatchToMuralhaPaulista === true)

      if (integrationLpr) {
        actionCard.outputId = integrationLpr?.lprIntegrationId
        actionCard.label = integrationLpr?.label
        actionCard.inputLabel = integrationLpr?.inputLabel
        actionCard.valueInput = action.integrationCode
        return actions.push(actionCard)
      }
      if (action.outputIntegrationId) {
        const serverEntity = servers.filter((it) => it.id === action.outputIntegrationId)[0]
        const serverType = serverEntity?.serverType?.description || ''
        actionCard.outputId = Number(action.outputIntegrationId)
        actionCard.label = serverType
        actionCard.inputLabel = serverType === 'One Portaria' ? 'Setor' : 'Partição'
        actionCard.valueInput = camera?.configuration.partition || ''
        return actions.push(actionCard)
      }
      if (action.telegramChatId) {
        actionCard.label = 'Telegram'
        actionCard.inputLabel = 'Id do chat'
        actionCard.valueInput = `${action.telegramChatId}`
        return actions.push(actionCard)
      }
      if (action.whatsappLink) {
        actionCard.label = 'WhatsApp'
        actionCard.inputLabel = 'Link do chat'
        actionCard.valueInput = action.whatsappLink
        return actions.push(actionCard)
      }
      if (action.dispatchToEmail) {
        actionCard.label = 'E-mail'
        actionCard.inputLabel = 'Enviar para'
        actionCard.valueInput = action.dispatchToEmail
        return actions.push(actionCard)
      }
      if (action.validationChatId || action.sigmaValidationId) {
        actionCard.label = 'Validar antes de despachar'
        actionCard.inputLabel = 'Enviar para'
        actionCard.valueInput = action.validationChatId ? 'Telegram' : 'Sigma'
        return actions.push(actionCard)
      }
      if (action.userIdHasNotification) {
        actionCard.label = 'Notificação ativada'
        actionCard.inputLabel = 'Enviar para'
        actionCard.valueInput = 'App Mobile'
        return actions.push(actionCard)
      }
      return actions.push(actionCard)
    })

    setActions(actions)

    if (editingObject && editingObject.interestArea) objectChosenToCreate.interestAreaId = editingObject.interestArea.id

    setObjectChosenToCreate(objectChosenToCreate)
    nextFunction(1, objectChosenToCreate.detectionTypeChosen!)
    nextFunction(2, objectChosenToCreate.type === 'line-crossing' ? 'Cruzamento de linha' : 'Detecção em uma área')
    if (skipThirdStep.includes(objectChosenToCreate.detectionTypeChosen!)) nextFunction(3, 'Concluído')
  }

  const shouldSkip = (skipArray: string[], detection: string) => {
    if (skipArray.includes(detection)) return true
    return false
  }

  const handleDifferentFlows = () => {
    const newSteps = [...steps]
    const detection = objectChosenToCreate?.detectionTypeChosen ?? ''

    newSteps[2].stepperStatus = shouldSkip(skipThirdStep, detection) ? 'disabled' : 'pending'
    newSteps[3].stepperStatus = shouldSkip(skipFourthStep, detection) ? 'disabled' : 'pending'

    if (isEdit) {
      if (newSteps[0].stepperStatus === 'pending') newSteps[0].stepperStatus = 'concluded'
      if (newSteps[1].stepperStatus === 'pending') newSteps[1].stepperStatus = 'concluded'
      if (shouldSkip(skipThirdStep, detection)) {
        newSteps[2].stepperStatus = 'concluded'
        newSteps[3].stepperStatus = 'active'
      } else {
        newSteps[2].stepperStatus = 'active'
      }
    }
    setSteps(newSteps)
  }

  useEffect(() => {
    if (!objectChosenToCreate) return
    const detection = objectChosenToCreate.detectionTypeChosen ?? ''

    populateOutput(detection)
  }, [objectChosenToCreate])

  useEffect(() => {
    if (isEdit && editingObject) {
      let detectionTypeChosen = editingObject.objectType.label
      if (editingObject.isObjectCounting) detectionTypeChosen = 'Relatório de Contagem'
      if (editingObject.isObjectPermanency) detectionTypeChosen = 'Tempo de Permanência'
      const detectionsOptionsSelected =
        editingObject.epiDetectionObject?.epis ??
        editingObject.vehicleDetectionObject?.vehicles ??
        editingObject.objectsDetectionObject?.objects ??
        editingObject.animalDetectionObject?.animals

      const objectChosenToCreate: ChosenObject = {
        ...editingObject,
        detectionTypeChosen,
        confidence: Number(editingObject.confidence),
        objectTypeId: editingObject.objectType.id,
        distance: editingObject.speedObject?.distance,
        speedLimit: editingObject.speedObject?.speedLimit,
        cameraId: editingObject.speedObject?.cameraId,
        speedObjectId: editingObject.speedObject?.id,
        detectionsOptionsSelected: detectionsOptionsSelected,
      } as ChosenObject

      const filteredActions: MappedActions | null = mappedActions.filter((it) => it.id === editingObject.id)[0]

      if (filteredActions) return populateActions(filteredActions, servers, objectChosenToCreate)

      if (editingObject && editingObject.interestArea) objectChosenToCreate.interestAreaId = editingObject.interestArea.id

      setObjectChosenToCreate(objectChosenToCreate)
      nextFunction(1, objectChosenToCreate.detectionTypeChosen!)
      nextFunction(2, objectChosenToCreate.interestAreaId ? 'Detecção em uma área' : 'Cruzamento de linha')
      if (skipThirdStep.includes(objectChosenToCreate.detectionTypeChosen!)) {
        nextFunction(3, 'Configurações')
        const newSteps = [...steps]
        newSteps[2].stepperStatus = 'concluded'
        setSteps(newSteps)
      }
    }
  }, [isEdit, editingObject, mappedActions])

  useEffect(() => {
    if (!isEdit) {
      setObjectChosenToCreate({
        detectionTypeChosen: '',
        confidence: 0.4,
        isActive: true,
        objectTypeId: 0,
        threshold: 1,
        distance: 0,
        cameraId: 0,
        coordinates: [],
        speedLimit: 0,
        type: 'interest-area',
        detectionsOptionsSelected: [],
      })
    }
  }, [])

  useEffect(() => {
    const detection = objectChosenToCreate?.detectionTypeChosen ?? ''
    const newSteps = [...steps]
    newSteps.map((it, index) => (it.children = ChildrenComponentEnum[index]))

    setSteps(newSteps)
    populateOutput(detection)
  }, [objectChosenToCreate, editingObject])

  useEffect(() => {
    handleDifferentFlows()
    findObjectType()
  }, [objectChosenToCreate?.detectionTypeChosen])

  const ChildrenComponentEnum: ChildrenComponentEnumType = useMemo(() => {
    return {
      0: (
        <DetectionTypeStep
          nextFunction={nextFunction}
          returnFunction={closeModal}
          setChosen={setDiffOnChosenObject}
          selected={objectChosenToCreate?.detectionTypeChosen}
        />
      ),
      1: detectionsToDrawArea.includes(objectChosenToCreate?.detectionTypeChosen!) ? (
        <DrawAreaStep nextFunction={nextFunction} returnFunction={returnFunction} setChosen={setDiffOnChosenObject} chosen={objectChosenToCreate} />
      ) : (
        <ChooseBarrierStep
          nextFunction={nextFunction}
          returnFunction={returnFunction}
          setChosen={setDiffOnChosenObject}
          chosen={objectChosenToCreate}
          registeredObjects={registeredObjects}
          detectionDisableLineCrossing={detectionDisableLineCrossing.includes(objectChosenToCreate?.detectionTypeChosen!)}
          hasLineCrossingPermission={hasReadLineCrossingPermission}
        />
      ),
      2: detectionsToDrawArea.includes(objectChosenToCreate?.detectionTypeChosen!) ? (
        <ConfigStepObjectPersonalized
          nextFunction={nextFunction}
          returnFunction={returnFunction}
          chosen={objectChosenToCreate}
          setChosen={setDiffOnChosenObject}
          isEdit={isEdit}
          camera={camera}
        />
      ) : (
        <ConfigStep
          nextFunction={nextFunction}
          returnFunction={returnFunction}
          chosen={objectChosenToCreate}
          registeredObjects={registeredObjects}
          finishFunction={finishFunction}
          setChosen={setDiffOnChosenObject}
          isEdit={isEdit}
          actions={actions}
        />
      ),
      3: (
        <CreateMultipleActions
          finishFunction={finishFunction}
          returnFunction={returnFunction}
          setChosen={setDiffOnChosenObject}
          chosen={objectChosenToCreate}
          isEdit={isEdit}
          outputServersItems={outputServersItems}
          setOutputServersItems={setOutputServersItems}
          actions={actions}
          updateTable={updateTable}
        />
      ),
    }
  }, [objectChosenToCreate, registeredObjects, outputServersItems, actions, editingObject])

  const [steps, setSteps] = useState<StepsTitles[]>([
    {
      stepperStageTitle: 'Passo 01',
      stepperTitle: 'Tipo de Detecção',
      stepperStatus: 'active',
      children: ChildrenComponentEnum[0],
    },
    {
      stepperStageTitle: 'Passo 02',
      stepperTitle: 'Tipo de Barreira',
      stepperStatus: 'pending',
      children: ChildrenComponentEnum[1],
    },
    {
      stepperStageTitle: 'Passo 03',
      stepperTitle: 'Configurações',
      stepperStatus: 'pending',
      children: ChildrenComponentEnum[2],
    },
    {
      stepperStageTitle: 'Passo 04',
      stepperTitle: 'Tipo de Ação',
      stepperStatus: 'pending',
      children: ChildrenComponentEnum[3],
    },
  ])

  return (
    <StepperModalContainer>
      <WarningModal isOpen={warningModalOpen} closeModal={() => setWarningModalOpen(false)} />
      <BtnClose onClose={() => closeModal(false)} />
      {objectChosenToCreate && (
        <Box width='100%' height='97%'>
          <Stepper steps={steps} />
        </Box>
      )}
    </StepperModalContainer>
  )
}

export default StepperModal
